<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import BankLogo from './BankLogo.vue';
import { BANK_OPTIONS } from './bankCatalog';
import { useStoreGetters } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  hooks: { type: Array, default: () => [] },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  actionUrl: {
    type: String,
    default: '',
  },
  actionRoute: {
    type: Object,
    default: null,
  },
});

const googleLogo = '/brand-assets/integrations/google.png';
const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { replaceInstallationName } = useBranding();

const connectedBanks = computed(() => {
  if (props.id !== 'banking') return [];
  return BANK_OPTIONS.filter(bank =>
    props.hooks.some(
      hook =>
        (hook.status === true || hook.status === 'enabled') &&
        (hook.settings?.provider === bank.value ||
          (bank.value === 'presenca_bank' &&
            hook.settings?.provider === 'prata'))
    )
  ).slice(0, 3);
});

const normalizedStatus = computed(() => {
  if (props.status) return props.status;
  return props.enabled ? 'active' : 'disabled';
});

const statusCopy = computed(() => {
  const map = {
    active: {
      label: 'Ativa',
      classes: 'border-n-teal-7 bg-n-teal-3/20 text-n-teal-10',
      dot: 'bg-n-teal-9',
    },
    disabled: {
      label: 'Inativa',
      classes: 'border-n-weak bg-n-alpha-1 text-n-slate-9',
      dot: 'bg-n-slate-7',
    },
    pending: {
      label: 'Pendente',
      classes: 'border-n-slate-7 bg-n-slate-3/50 text-n-slate-11',
      dot: 'bg-n-slate-8',
    },
    error: {
      label: 'Com erro',
      classes: 'border-n-ruby-8/50 bg-n-ruby-4/20 text-n-ruby-10',
      dot: 'bg-n-ruby-9',
    },
  };

  return map[normalizedStatus.value] || map.disabled;
});

const actionTo = computed(() => {
  if (props.actionRoute) return props.actionRoute;

  return frontendURL(
    props.actionUrl ||
      `accounts/${accountId.value}/settings/integrations/${props.id}`
  );
});

const fallbackIcons = {
  stripe: 'i-brand-stripe',
  microsoft: 'i-brand-microsoft',
  google: 'i-brand-google',
  meta: 'i-brand-meta',
  banking: 'i-lucide-landmark',
  corban: 'i-lucide-handshake',
  erp: 'i-lucide-database',
  google_calendar: 'i-brand-google-calendar',
  email_inbox: 'i-lucide-mail-plus',
  webhook: 'i-lucide-webhook',
  openai: 'i-brand-openai',
  slack: 'i-brand-slack',
  shopify: 'i-brand-shopify',
  notion: 'i-brand-notion',
  linear: 'i-brand-linear',
};

const fallbackIcon = computed(() => fallbackIcons[props.id]);
const actionLabel = computed(() =>
  normalizedStatus.value === 'active' ? 'Gerenciar' : 'Configurar'
);

const categories = computed(() => {
  const map = {
    banking: ['Bancos', 'Produtos'],
    corban: ['Corban', 'Consultas'],
    erp: ['ERP', 'Financeiro'],
    asaas: ['Cobranças', 'IA'],
    google_calendar: ['Agenda', 'Produtividade'],
    email_inbox: ['Email', 'Atendimento'],
    webhook: ['API', 'Eventos'],
    openai: ['IA', 'Atendimento'],
    slack: ['Equipe', 'Mensagens'],
    shopify: ['Comercio', 'Pedidos'],
    notion: ['Docs', 'Workspace'],
    linear: ['Projetos', 'Issues'],
  };

  return map[props.id] || ['Integração'];
});
</script>

<template>
  <article
    class="group relative cursor-pointer flex min-h-[220px] flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-n-brand/50 hover:shadow-lg"
  >
    <router-link
      :to="actionTo"
      :aria-label="`${actionLabel}: ${name}`"
      class="absolute inset-0 z-10 cursor-pointer rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-n-brand"
    />
    <div
      class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-n-blue-8/70 to-transparent opacity-0 transition group-hover:opacity-100"
    />

    <div class="flex flex-1 flex-col p-4">
      <div class="mb-4 flex items-start justify-between gap-3">
        <div
          v-if="connectedBanks.length"
          class="bank-fan"
          role="img"
          :aria-label="`Bancos ativos: ${connectedBanks.map(bank => bank.label).join(', ')}`"
        >
          <BankLogo
            v-for="(bank, index) in connectedBanks"
            :key="bank.value"
            :provider="bank.value"
            class="bank-fan__logo"
            :style="{ '--bank-index': index }"
          />
        </div>
        <div
          v-else-if="id === 'erp'"
          class="flex gap-2"
          role="img"
          aria-label="IXCSoft e MkAuth"
        >
          <span
            v-for="provider in ['ixcsoft', 'mkauth']"
            :key="provider"
            class="grid size-10 place-items-center rounded-lg bg-white ring-1 ring-n-weak"
          >
            <img
              :src="`/brand-assets/integrations/${provider}.${provider === 'ixcsoft' ? 'webp' : 'png'}`"
              alt=""
              class="size-8 object-contain"
            />
          </span>
        </div>
        <BankLogo
          v-else-if="id === 'asaas'"
          provider="asaas"
          class="!size-10"
        />
        <div
          v-else-if="id === 'google_calendar'"
          class="grid size-10 place-items-center rounded-lg bg-white ring-1 ring-n-weak"
        >
          <img :src="googleLogo" alt="Google" class="size-7 object-contain" />
        </div>
        <div
          v-else-if="fallbackIcon"
          class="flex h-10 w-10 items-center justify-center rounded-lg border border-n-brand/20 bg-n-brand/10 text-n-brand shadow-sm transition group-hover:border-n-brand/50 group-hover:bg-n-brand/15"
        >
          <span :class="fallbackIcon" class="size-4" />
        </div>
        <div
          v-else
          class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 p-1.5"
        >
          <img
            :src="`/dashboard/images/integrations/${id}.png`"
            class="max-w-full rounded-lg block dark:hidden"
            alt=""
          />
          <img
            :src="`/dashboard/images/integrations/${id}-dark.png`"
            class="max-w-full rounded-lg hidden dark:block"
            alt=""
          />
        </div>

        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold"
            :class="statusCopy.classes"
          >
            <span class="h-1.5 w-1.5 rounded-full" :class="statusCopy.dot" />
            {{ statusCopy.label }}
          </span>
        </div>
      </div>

      <div class="flex flex-1 flex-col">
        <h3 class="text-sm font-semibold leading-snug text-n-slate-12">
          {{ name }}
        </h3>
        <p class="mt-2 line-clamp-2 text-xs leading-5 text-n-slate-10">
          {{ replaceInstallationName(description) }}
        </p>

        <div class="mt-3 flex flex-wrap gap-1.5">
          <span
            v-for="category in categories"
            :key="category"
            class="rounded-md border border-n-weak bg-n-alpha-2 px-2 py-0.5 text-[10px] font-semibold text-n-slate-10"
          >
            {{ category }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-between gap-3 border-t border-n-weak bg-n-solid-2 px-4 py-3"
    >
      <span class="inline-flex items-center gap-1.5 text-[11px] text-n-slate-9">
        <span class="i-lucide-shield-check size-3.5" />
        Configuração protegida
      </span>
      <span
        class="i-hugeicons-arrow-right-02 size-4 text-n-slate-11"
        aria-hidden="true"
      />
    </div>
  </article>
</template>

<style scoped>
.bank-fan {
  position: relative;
  width: 6rem;
  height: 3rem;
  flex-shrink: 0;
}
.bank-fan__logo {
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  top: 0.2rem;
  left: calc(var(--bank-index) * 1.35rem);
  transform: rotate(calc((var(--bank-index) - 1) * 12deg));
  z-index: calc(3 - var(--bank-index));
  box-shadow:
    0 0 0 2px rgb(var(--slate-1)),
    0 3px 6px rgb(0 0 0 / 0.12);
}
.bank-fan__logo:only-child {
  transform: none;
}
</style>
